import { Key, degreePc } from './key';
import { Pitch, diatonicIndex, midiOf, mod, pcNumber } from './pitch';

/**
 * Figured bass computed from the notes themselves (the period lens).
 * Returns figures from top to bottom, e.g. ["6", "5"] or ["♯4", "2"].
 */
export function figuredBass(pitches: Pitch[], key: Key): string[] {
  if (pitches.length === 0) return [];
  const sorted = [...pitches].sort((a, b) => midiOf(a) - midiOf(b));
  const bass = sorted[0];
  const byNumber = new Map<number, string>();
  for (const p of sorted.slice(1)) {
    const generic = mod(diatonicIndex(p) - diatonicIndex(bass), 7) + 1;
    if (generic === 1) continue; // octave doublings are not figured
    // Accidental shown when the note departs from the key signature.
    const expected = degreePc({ tonic: key.tonic, mode: key.mode }, mod(p.letter - key.tonic.letter, 7) + 1);
    const diff = mod(pcNumber(p) - pcNumber(expected), 12);
    const acc = diff === 0 ? '' : diff === 1 || diff === 2 ? '♯' : '♭';
    const natural = diff !== 0 && expected.alter !== 0 && p.alter === 0 ? '♮' : '';
    byNumber.set(generic, (natural || acc) + String(generic));
  }
  const nums = [...byNumber.keys()].sort((a, b) => b - a);
  const plain = nums.join(',');
  const abbreviations: Record<string, number[]> = {
    '5,3': [],
    '6,3': [6],
    '6,4': [6, 4],
    '7,5,3': [7],
    '6,5,3': [6, 5],
    '6,4,3': [4, 3],
    '6,4,2': [4, 2],
    '7,3': [7],
    '5,4': [5, 4],
    '7,4': [7, 4],
  };
  const shown = abbreviations[plain] ?? nums;
  // Keep any accidental attached to a figure that the abbreviation drops (e.g. ♯6 in "♯6/4/3").
  const out = shown.map((n) => byNumber.get(n) ?? String(n));
  for (const n of nums) {
    const label = byNumber.get(n)!;
    if (!shown.includes(n) && /[♯♭♮]/.test(label)) out.push(label.replace(/\d+/, '') + (n === 3 ? '' : n));
  }
  return out.filter((f) => f !== '');
}

/** Pitch-class numbers a figured bass implies above a bass note in a key. */
export function figuresToPcs(bass: Pitch, figures: number[], key: Key, alterations: Record<number, number> = {}): number[] {
  const out = [pcNumber(bass)];
  for (const f of figures) {
    const letter = mod(bass.letter + f - 1, 7);
    const diatonic = degreePc(key, mod(letter - key.tonic.letter, 7) + 1);
    out.push(mod(pcNumber(diatonic) + (alterations[f] ?? 0), 12));
  }
  return [...new Set(out)];
}
